import React, { useEffect, useState } from 'react';
import { Card, Row, Col } from 'antd';
import {
  DashboardOutlined, SearchOutlined, CalendarOutlined,
  FileTextOutlined, TeamOutlined, AimOutlined, BuildOutlined,
  ThunderboltOutlined, UserOutlined, BarChartOutlined,
} from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import api from '../api/client';

const pages = [
  {
    icon: <DashboardOutlined />,
    title: 'Dashboard',
    who: 'Both',
    desc: 'Your action inbox. Tutors see gate submissions to review, students still to onboard, and upcoming exams. Students see what needs rework, exam countdowns, and updates from their tutors.',
  },
  {
    icon: <SearchOutlined />,
    title: 'Find Tutors',
    who: 'Students',
    desc: 'Browse and filter tutors by name, gender, level, subject, or age — or pick a subject and let SGLearner recommend matching tutors. Click any card to view a profile and subscribe.',
  },
  {
    icon: <CalendarOutlined />,
    title: 'Schedule',
    who: 'Both',
    desc: 'Set your available time slots on a calendar. Supports recurring slots (daily, weekly, biweekly, monthly) so students know when you\'re free.',
  },
  {
    icon: <FileTextOutlined />,
    title: 'Materials',
    who: 'Both',
    desc: 'Tutors can upload PDFs and images for their students, rename files, and generate content with AI. Students can view and download everything shared with them.',
  },
  {
    icon: <TeamOutlined />,
    title: 'Subscriptions',
    who: 'Both',
    desc: 'Students subscribe to a tutor\'s specific subject from the tutor\'s profile. This page shows all your active subscriptions — students can cancel, tutors see who\'s enrolled.',
  },
  {
    icon: <AimOutlined />,
    title: 'Roadmap',
    who: 'Both',
    desc: 'A structured learning roadmap broken into stages. Students submit answers at each gate; tutors review and approve or send back for revision before the next stage unlocks.',
  },
  {
    icon: <BarChartOutlined />,
    title: 'Analytics',
    who: 'Both',
    desc: 'Tutors see student completion bars, stage pass rates, and a 14-day gate-activity trend. Students see overall progress, per-assignment donuts, and a stage-status breakdown.',
  },
  {
    icon: <BuildOutlined />,
    title: 'Roadmap Builder',
    who: 'Tutors',
    desc: 'Create and customise Roadmap templates for your subjects. Add, reorder, and edit stages with criteria and difficulty levels. Copy from system templates as a starting point.',
  },
  {
    icon: <ThunderboltOutlined />,
    title: 'AI Usage',
    who: 'Tutors',
    desc: 'Track your AI content generation activity across question generation, worked solutions, summary notes, and exam papers. Currently free during beta.',
  },
  {
    icon: <UserOutlined />,
    title: 'Profile',
    who: 'Both',
    desc: 'Update your name, bio, profile picture, and cover photo. Students can set their school and current school year. Tutors can manage their subjects, levels, and monthly rates.',
  },
];

const WHO_COLOUR = { Both: '#595959', Tutors: '#0958d9', Students: '#389e0d' };

export default function About() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/api/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const statItems = [
    { label: 'Students',             value: stats?.students },
    { label: 'Tutors',               value: stats?.tutors },
    { label: 'Active subscriptions', value: stats?.subscriptions },
  ];

  return (
    <AppLayout>
      {/* Hero */}
      <Card variant="outlined" style={{ borderColor: '#e8e8e8', marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#bbb', marginBottom: 10 }}>
          About
        </div>
        <h1 style={{ margin: 0, marginBottom: 8, fontSize: 22, fontWeight: 700, color: '#0a0a0a' }}>SGLearner</h1>
        <p style={{ color: '#666', margin: 0, fontSize: 14, lineHeight: 1.8, maxWidth: 560 }}>
          A platform built to make quality tutoring more accessible across Singapore.
          Whether you're a student looking for the right tutor, or a tutor wanting to manage your
          schedule and share resources — SGLearner brings everything into one place.
        </p>

        <div style={{
          display: 'flex', gap: 0, marginTop: 28,
          paddingTop: 24, borderTop: '1px solid #f0f0f0',
          flexWrap: 'wrap',
        }}>
          {statItems.map((s, i) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && <div style={{ width: 1, height: 36, background: '#e8e8e8', margin: '0 28px' }} />}
              <div>
                <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: '#0a0a0a' }}>
                  {s.value != null ? s.value : '—'}
                </div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Features */}
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#aaa', marginBottom: 12 }}>
        Features
      </div>
      <Row gutter={[16, 16]}>
        {pages.map(p => (
          <Col xs={24} sm={12} md={8} key={p.title}>
            <Card variant="outlined" style={{ borderColor: '#e8e8e8', height: '100%' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 8, background: '#f5f5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, fontSize: 16, color: '#0a0a0a',
                }}>
                  {p.icon}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</span>
                    <span style={{
                      fontSize: 10, color: WHO_COLOUR[p.who],
                      border: `1px solid ${WHO_COLOUR[p.who]}`,
                      borderRadius: 4, padding: '0 5px', lineHeight: '18px',
                    }}>
                      {p.who}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>{p.desc}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </AppLayout>
  );
}
